/** ProjectStagesService — loads the project_stages reference table once and pushes the
 * rows into the stageUtils overlay so getStageLabel/Color/Icon reflect DB-defined labels.
 * Stage keys are stable; labels are editable. Falls back to the hard-coded STAGE_INFO
 * (via stageUtils) whenever the table has not loaded or the request fails, so the UI never
 * renders raw snake_case. */

import { API_BASE, getSupabaseClient } from '../utils/supabase/client';
import { applyStageOverlay } from '../utils/stageUtils';

export interface ProjectStage {
  key: string;
  label: string;
  sortOrder: number;
  color: string | null;
  icon: string | null;
  isActive: boolean;
}

let _cache: ProjectStage[] | null = null;
let _inFlight: Promise<ProjectStage[]> | null = null;

async function getAuthToken(): Promise<string> {
  const { data: { session } } = await getSupabaseClient().auth.getSession();
  return session?.access_token ?? '';
}

function mapRow(row: Record<string, unknown>): ProjectStage {
  return {
    key: row.key as string,
    label: row.label as string,
    sortOrder: (row.sortOrder as number) ?? 0,
    color: (row.color as string | null) ?? null,
    icon: (row.icon as string | null) ?? null,
    isActive: (row.isActive as boolean) ?? true,
  };
}

export const ProjectStagesService = {
  /** Synchronous read of the last successful load — null before the first fetch resolves. */
  getCached(): ProjectStage[] | null {
    return _cache;
  },

  /** Fetch the stage table and apply it to the stageUtils overlay. Safe to call repeatedly;
   * concurrent calls share one request. On failure returns [] and leaves STAGE_INFO as the
   * fallback. */
  async load(): Promise<ProjectStage[]> {
    if (_inFlight) return _inFlight;

    _inFlight = (async () => {
      try {
        const token = await getAuthToken();
        const res = await fetch(`${API_BASE}/project-stages`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(await res.text());
        const data = (await res.json()) as { stages?: Record<string, unknown>[] };
        const stages = (data.stages ?? []).map(mapRow);
        _cache = stages;
        applyStageOverlay(stages);
        return stages;
      } catch (error) {
        console.warn('[ProjectStagesService] load failed, using STAGE_INFO fallback:', error);
        return [];
      } finally {
        _inFlight = null;
      }
    })();

    return _inFlight;
  },
};
